import { useState } from 'react'

import DownloadIcon from '@mui/icons-material/Download'
import NoteAltIcon from '@mui/icons-material/NoteAlt'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import { styled } from '@mui/styles'
import { GridRowId } from '@mui/x-data-grid'
import { toast } from 'react-toastify'

import { AccountPayableTabHash } from 'constants/AccountPayableStatusCategories'
import {
  AccountPayableRecordStatus,
  GetAllAccountPayableRecordsDocument,
  GetAllAccountPayableRecordsQueryVariables,
  useRefreshPaymentStatusMutation,
  useExportSmartPayQueriesMutation,
} from 'generated/graphql'
import { SaveFile } from 'helpers'

import { UploadSmartPayCsv, UploadSmartPayInputFile } from './UploadSmartPayCsv'

const StyledTypography = styled(Typography)({
  fontSize: '13px',
  color: 'black',
})

const StyledDownloadIcon = styled(DownloadIcon)({
  fontSize: '13px',
})

const StyledRefreshButton = styled(Typography)({
  fontSize: '13px',
  borderStyle: 'solid',
  borderColor: '#9c27b0',
  border: '1px',
  padding: '5px',
  borderRadius: '5px',
})

const StyledNoteAltIcon = styled(NoteAltIcon)({
  fontSize: '13px',
})

interface IProps {
  handleOpenModal: any
  handleOpenManagePaymentShedule: any
  selectedItems?: GridRowId[]
  handleCreateBatch: () => void
  createBatchLoading: boolean
}

const ActionOptions = ({
  handleOpenModal,
  handleOpenManagePaymentShedule,
  selectedItems = [],
  handleCreateBatch,
  createBatchLoading,
}: IProps) => {
  const tab = window.location.href.split('#')[1]
  const [uploadCsvOpen, setUploadCsvOpen] = useState(false)
  const [uploadSmartPayInputFileOpen, setUploadSmartPayInputFileOpen] = useState(false)

  const isNotUnderReviewTab = tab !== AccountPayableTabHash.UnderReview
  const isFlagged = tab === AccountPayableTabHash.Flagged
  const isPaymentSent = tab === AccountPayableTabHash.PaymentSent
  const isQueriesTab = tab === AccountPayableTabHash.Queries
  const isArchivedTab = tab === AccountPayableTabHash.Archive
  const disabledOption = selectedItems?.length === 0
  const isUnderReviewTab = tab === AccountPayableTabHash.UnderReview
  const isSmartPayExportTab = isQueriesTab || isArchivedTab
  const isCreateBatchTab =
    tab === AccountPayableTabHash.ScheduledPayment || tab === AccountPayableTabHash.Archive

  const [exportSmartPayQueries, { loading: exportLoading }] = useExportSmartPayQueriesMutation()

  const handleExportSmartPay = async () => {
    try {
      const result = await exportSmartPayQueries({
        variables: {
          accountPayableRecordIds: selectedItems.map((id) => Number(id)),
        },
      })

      const csvContent = result.data?.exportSmartPayQueries

      if (csvContent) {
        const fileName = `smartpay_queries_${new Date().toISOString().split('T')[0]}.csv`
        SaveFile(csvContent, fileName)
        toast.success('SmartPay queries exported successfully')
      }
    } catch (error) {
      toast.error('Error exporting SmartPay queries')
      console.error(error)
    }
  }

  const [refreshPaymentStatus, { loading: paymentsStatusloading }] =
    useRefreshPaymentStatusMutation({
      fetchPolicy: 'no-cache',
      refetchQueries: [
        {
          query: GetAllAccountPayableRecordsDocument,
          variables: {
            where: {
              status: {
                in: [
                  AccountPayableRecordStatus.PaymentSent,
                  AccountPayableRecordStatus.Archived,
                  AccountPayableRecordStatus.PaymentFailed,
                ],
              },
            },
          } as GetAllAccountPayableRecordsQueryVariables,
        },
      ],
    })

  const onWholePageRefresh = async () => {
    const result = await refreshPaymentStatus({
      variables: {
        input: {
          all: true,
        },
      },
    })

    if (result.data?.refreshPaymentStatus.length) {
      toast.success(`${result.data.refreshPaymentStatus.length} payments were refreshed.`)
    } else {
      toast.error('Something went wrong. Please try again.')
    }
  }

  return (
    <>
      <Box
        className='d-flex'
        sx={{
          p: 1,
          mb: 1,
          border: 1,
          borderColor: grey[300],
          borderRadius: '5px',
        }}>
        <LoadingButton
          onClick={isSmartPayExportTab ? handleExportSmartPay : undefined}
          disabled={isSmartPayExportTab && disabledOption}
          loading={exportLoading}>
          <StyledDownloadIcon />
          <StyledTypography>{isSmartPayExportTab ? 'Export SmartPay' : 'Export'}</StyledTypography>
        </LoadingButton>
        {isArchivedTab && (
          <Button
            onClick={() => setUploadSmartPayInputFileOpen(true)}>
            <StyledDownloadIcon />
            <StyledTypography>{'Export SmartPay (from CSV)'}</StyledTypography>
          </Button>
        )}
        <Button onClick={() => isUnderReviewTab && setUploadCsvOpen(true)}>
          <StyledDownloadIcon />
          <StyledTypography>
            {isUnderReviewTab ? 'Upload SmartPay CSV' : 'Download Documents'}
          </StyledTypography>
        </Button>
        <Button>
          <StyledNoteAltIcon />
          <StyledTypography>Send Reminder</StyledTypography>
        </Button>
        {!isNotUnderReviewTab && (
          <Button onClick={handleOpenModal} disabled={disabledOption}>
            <StyledNoteAltIcon />
            <StyledTypography>Smart Approve</StyledTypography>
          </Button>
        )}
        <Button>
          <StyledNoteAltIcon />
          <StyledTypography>Schedule for payment</StyledTypography>
        </Button>
        {(!isNotUnderReviewTab || isFlagged) && (
          <Button onClick={handleOpenManagePaymentShedule} disabled={disabledOption}>
            <StyledNoteAltIcon />
            <StyledTypography>Manage payment schedule</StyledTypography>
          </Button>
        )}
        <Button>
          <StyledNoteAltIcon />
          <StyledTypography>Mark as paid</StyledTypography>
        </Button>        <Button>
          <StyledNoteAltIcon />
          <StyledTypography>Archive</StyledTypography>
        </Button>
        <Button>
          <StyledTypography>Batch invoice</StyledTypography>
        </Button>
        {isCreateBatchTab && (
          <LoadingButton
            onClick={handleCreateBatch}
            disabled={disabledOption}
            loading={createBatchLoading}>
            <StyledNoteAltIcon />
            <StyledTypography>Create Batch</StyledTypography>
          </LoadingButton>
        )}
        {isPaymentSent && (
          <LoadingButton loading={paymentsStatusloading} onClick={onWholePageRefresh}>
            <StyledRefreshButton>Refresh</StyledRefreshButton>
          </LoadingButton>
        )}
      </Box>

      <UploadSmartPayCsv open={uploadCsvOpen} onClose={() => setUploadCsvOpen(false)} />
      <UploadSmartPayInputFile open={uploadSmartPayInputFileOpen} onClose={() => setUploadSmartPayInputFileOpen(false)} />
    </>
  )
}

export default ActionOptions
